import React, { useRef, useState, ChangeEvent, FormEvent, KeyboardEvent } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { loginWorker, registerWorker, resendEmailVerification, requestPasswordReset, TURNSTILE_SITE_KEY } from "../api";
import { LoginResponse } from "../types";
import detailMessage, { isNetworkError } from '../detailMessage';
import ServiceUnavailablePage from '../components/ServiceUnavailablePage';
import Logo from "../components/Logo";
import AuthControls from "../components/AuthControls";
import { useLang } from "../LangContext";

interface RegisterData {
  first_name: string;
  last_name: string;
  e_mail: string;
  password: string;
}

interface Props {
  onAuthenticated: (res: LoginResponse) => void;
}

type View = 'login' | 'register' | 'forgot';

export default function LoginPage({ onAuthenticated }: Props) {
  const [view, setView] = useState<View>('login');
  const [backendDown, setBackendDown] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registerData, setRegisterData] = useState<RegisterData>({
    first_name: "",
    last_name: "",
    e_mail: "",
    password: "",
  });
  const [registerMessage, setRegisterMessage] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);
  const [loginErrorCode, setLoginErrorCode] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [forgotTurnstileToken, setForgotTurnstileToken] = useState("");
  const forgotTurnstileRef = useRef<TurnstileInstance>(null);
  const { t } = useLang();
  // Backend tags the unverified-email case with code "email_unverified" on the
  // 401 body, so the resend UX doesn't depend on the error prose.
  const isUnverifiedError = loginErrorCode === "email_unverified";

  const login = async () => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    setLoginError("");
    setLoginErrorCode("");
    setVerificationResent(false);
    try {
      const res = await loginWorker(email.trim(), password.trim());
      onAuthenticated(res);
      // If onAuthenticated transitions to MFA, this component stays mounted
      // until the parent unmounts it; reset the spinner so the form is usable
      // again if the user backs out.
      if (res.mfa_required === true) {
        setIsLoggingIn(false);
      }
    } catch (err: unknown) {
      console.error("Login failed", err);
      if (isNetworkError(err)) {
        setBackendDown(true);
      } else {
        setLoginError(detailMessage(err, t.loginFailed));
        setLoginErrorCode((err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? "");
      }
      setIsLoggingIn(false);
    }
  };

  const resendVerification = async () => {
    if (resendingVerification || !email) return;
    setResendingVerification(true);
    try {
      await resendEmailVerification(email.trim());
    } catch (err) {
      // The endpoint is anti-enumeration: it never throws on unknown accounts.
      // Anything that does land here is a transport/server problem — log it
      // but keep the same generic UX.
      console.error("Resend verification failed", err);
    } finally {
      setVerificationResent(true);
      setResendingVerification(false);
    }
  };

  const switchTo = (next: View) => {
    setView(next);
    setLoginError("");
    setRegisterMessage("");
    setRegisterError("");
  };

  const submitForgot = async (e: FormEvent) => {
    e.preventDefault();
    setForgotSubmitting(true);
    try {
      await requestPasswordReset(forgotEmail, forgotTurnstileToken);
    } catch {
      // anti-enumeration: always show success
      forgotTurnstileRef.current?.reset();
      setForgotTurnstileToken("");
    }
    setForgotDone(true);
    setForgotSubmitting(false);
  };

  const handleRegisterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    setRegisterError("");
    setRegisterMessage("");

    const data = await registerWorker(
      registerData.first_name.trim(),
      registerData.last_name.trim(),
      registerData.e_mail.trim(),
      registerData.password.trim(),
      turnstileToken,
    );
    if (!data.message) {
      setRegisterError(data.detail || t.registrationFailed);
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } else {
      setRegisterMessage(
        data.message ||
          "Registration successful. Check your email to verify your address.",
      );
      setEmail(registerData.e_mail);
      setPassword(registerData.password);
      setView('login');
    }
  };

  if (backendDown) {
    return <ServiceUnavailablePage onRetry={() => setBackendDown(false)} />;
  }

  return (
    <main className="login-container">
      <div className="login-wrapper">
      <div className="login-top-controls">
        <AuthControls />
      </div>
      <div className="login-card">
        <div className="login-brand">
          <Logo size={48} withWordmark />
        </div>

        {view === 'login' && (
          <div className="login-form">
            <input
              className="input"
              placeholder={t.loginEmailPlaceholder}
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && login()}
              disabled={isLoggingIn}
            />
            <input
              className="input"
              placeholder={t.loginPasswordPlaceholder}
              type="password"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && login()}
              disabled={isLoggingIn}
            />
            <button
              className="button login-button"
              onClick={login}
              disabled={isLoggingIn || !email || !password}
            >
              {isLoggingIn ? (
                <>
                  <span className="login-spinner" aria-hidden="true" />
                  {t.loggingIn}
                </>
              ) : (
                t.loginButton
              )}
            </button>
            {loginError && <div className="error-message">{loginError}</div>}
            {isUnverifiedError && !verificationResent && (
              <button
                type="button"
                className="link-button"
                onClick={resendVerification}
                disabled={resendingVerification || !email}
              >
                {resendingVerification ? t.sendingVerification : t.resendVerification}
              </button>
            )}
            {verificationResent && (
              <div className="success-message">{t.verificationResentMessage}</div>
            )}
            {registerMessage && <div className="success-message">{registerMessage}</div>}
            <p>
              <button type="button" className="link-button" onClick={() => switchTo('forgot')}>
                {t.forgotPassword}
              </button>
            </p>
            <p>
              {t.noAccount}{" "}
              <button className="link-button" onClick={() => switchTo('register')}>
                {t.registerHere}
              </button>
            </p>
          </div>
        )}

        {view === 'register' && (
          <form className="register-form" onSubmit={handleRegister}>
            <input
              className="input"
              name="first_name"
              placeholder={t.registerFirstName}
              value={registerData.first_name}
              onChange={handleRegisterChange}
              required
            />
            <input
              className="input"
              name="last_name"
              placeholder={t.registerLastName}
              value={registerData.last_name}
              onChange={handleRegisterChange}
              required
            />
            <input
              className="input"
              name="e_mail"
              placeholder={t.loginEmailPlaceholder}
              value={registerData.e_mail}
              onChange={handleRegisterChange}
              required
            />
            <input
              className="input"
              name="password"
              placeholder={t.loginPasswordPlaceholder}
              type="password"
              value={registerData.password}
              onChange={handleRegisterChange}
              required
            />
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onExpire={() => setTurnstileToken("")}
              onError={() => setTurnstileToken("")}
              options={{ theme: "auto" }}
            />
            <button className="button" type="submit" disabled={!turnstileToken}>{t.registerButton}</button>
            {registerMessage && <div className="success-message">{registerMessage}</div>}
            {registerError && <div className="error-message">{registerError}</div>}
            <p>
              {t.alreadyHaveAccount}{" "}
              <button type="button" className="link-button" onClick={() => switchTo('login')}>
                {t.backToLogin}
              </button>
            </p>
          </form>
        )}

        {view === 'forgot' && (
          <div className="login-form">
            {forgotDone ? (
              <>
                <div className="success-message">{t.resetLinkSentMessage}</div>
                <p>
                  <button type="button" className="link-button" onClick={() => switchTo('login')}>
                    {t.backToLogin}
                  </button>
                </p>
              </>
            ) : (
              <form onSubmit={submitForgot} style={{ display: "contents" }}>
                <p style={{ margin: "0 0 0.5rem", color: "var(--text-secondary)", fontSize: "0.875rem" }}>
                  {t.forgotPasswordSubtitle}
                </p>
                <input
                  className="input"
                  type="email"
                  required
                  placeholder={t.loginEmailPlaceholder}
                  value={forgotEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForgotEmail(e.target.value)}
                  disabled={forgotSubmitting}
                />
                <Turnstile
                  ref={forgotTurnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setForgotTurnstileToken}
                  onExpire={() => setForgotTurnstileToken("")}
                  onError={() => setForgotTurnstileToken("")}
                  options={{ theme: "auto" }}
                />
                <button className="button" type="submit" disabled={forgotSubmitting || !forgotEmail || !forgotTurnstileToken}>
                  {forgotSubmitting ? t.sendingVerification : t.sendResetLink}
                </button>
                <p>
                  <button type="button" className="link-button" onClick={() => switchTo('login')}>
                    {t.backToLogin}
                  </button>
                </p>
              </form>
            )}
          </div>
        )}
      </div>
      </div>
    </main>
  );
}
